import { useEffect, useRef, useState } from 'react';


import {
  collection,
  addDoc,
  getDocs,
  deleteDoc,
  updateDoc,
  doc
  } from 'firebase/firestore';

import { createClientUser } from './createClientUser';

import { db } from './firebase';

import Sidebar from './components/Sidebar';
import SplashScreen from './components/SplashScreen';

import Dashboard from './components/Dashboard';
import Projects from './components/Projects';

import Actors from './components/Actors';
import ActorForm from './components/ActorForm';

import AlertForm from './components/AlertForm';
import Alerts from './components/Alerts';

import MapSection from './components/MapSection';
import TerritorialPanel from './components/TerritorialPanel';

import TimelineForm from './components/TimelineForm';
import Timeline from './components/Timeline';

import SocialMonitoring from './components/SocialMonitoring';

import ExecutiveReport from './components/ExecutiveReport';
import AnalyticsPanel from './components/AnalyticsPanel';
import GlobalSearch from './components/GlobalSearch';
import KnowledgeGraph from './components/KnowledgeGraph';

import { useAuth } from './AuthContext';

import { useVitaEngine } from './hooks/useVitaEngine';
import { useCognitiveAlerts } from './hooks/useCognitiveAlerts';
import CognitiveAlerts from './components/CognitiveAlerts';
import { saveActorSnapshot } from './utils/saveActorSnapshot';
import { buildProjectPrediction } from './utils/buildPredictiveEngine';


function VitaApp() {
  const { currentUser, userRol, logout } = useAuth();

  const mainRef = useRef(null);
  // eslint-disable-next-line no-unused-vars
  const executiveReportRef = useRef(null);
  const actorFormRef = useRef(null);
  const actorMapRef = useRef(null);
  const alertFormRef = useRef(null);
  const timelineFormRef = useRef(null);
  const socialFormRef = useRef(null);
  const dashboardMapRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState('dashboard');

  const handleNavClick = (view) => {
    if (view === activeView) {
      mainRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      setActiveView(view);
    }
  };
  const [selectedOperationalItem, setSelectedOperationalItem] = useState(null);

  const [territorialFilters, setTerritorialFilters] = useState({
    selectedRegion: '',
    selectedComuna: '',
    selectedSector: '',
    selectedFoco: ''
  });

  const [projects, setProjects] = useState([]);
  const [activeProjectId, setActiveProjectId] = useState('');

  const [actors, setActors] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [timelineEvents, setTimelineEvents] = useState([]);
  const [socialSignals, setSocialSignals] = useState([]);
  const [snapshots, setSnapshots] = useState([]);

  const [editingActorId, setEditingActorId] = useState(null);
  const [showActorCreation, setShowActorCreation] = useState(false);

  const [editingAlertId, setEditingAlertId] = useState(null);
  const [editingTimelineId, setEditingTimelineId] = useState(null);

  const [newProject, setNewProject] = useState({
    nombre: '',
    cliente: '',
    tipo: '',
    region: '',
    regionCodigo: '',
    estado: '',
    descripcion: ''
  });

  const [newActor, setNewActor] = useState({
    nombre: '',
    tipo: '',
    riesgo: '',
    estado: '',
    comuna: '',
    sector: '',
    influencia: '',
    observaciones: '',
    latitud: '',
    longitud: '',
    tags: '',
    telefono: '',
    correo: '',
    whatsapp: '',
    sitioWeb: '',
    facebook: '',
    instagram: '',
    xTwitter: '',
    linkedin: ''
  });

  const [newAlert, setNewAlert] = useState({
    titulo: '',
    nivel: '',
    actor: '',
    descripcion: '',
    estado: '',
    fecha: '',
    latitud: '',
    longitud: '',
    tags: ''
  });

  const [newTimeline, setNewTimeline] = useState({
    titulo: '',
    fecha: '',
    tipo: '',
    actor: '',
    descripcion: ''
  });

  const [newSignal, setNewSignal] = useState({
    actor: '',
    red: '',
    sentimiento: '',
    riesgo: '',
    impacto: '',
    tipo: '',
    ubicacion: '',
    link: '',
    fecha: '',
    texto: '',
    latitud: '',
    longitud: '',
    tags: ''
  });

  const cargarProyectos = async () => {
    const querySnapshot = await getDocs(collection(db, 'proyectos'));

    const data = querySnapshot.docs.map((documento) => ({
      id: documento.id,
      ...documento.data()
    }));

    setProjects(data);

    if (!activeProjectId && data.length > 0) {
      setActiveProjectId(data[0].id);
    }
  };

  const cargarActores = async () => {
    const querySnapshot = await getDocs(collection(db, 'actores'));

    const data = querySnapshot.docs.map((documento) => ({
      id: documento.id,
      ...documento.data()
    }));

    setActors(data);
  };

  const cargarAlertas = async () => {
    const querySnapshot = await getDocs(collection(db, 'alertas'));

    const data = querySnapshot.docs.map((documento) => ({
      id: documento.id,
      ...documento.data()
    }));

    setAlerts(data);
  };

  const cargarTimeline = async () => {
    const querySnapshot = await getDocs(collection(db, 'timeline'));

    const data = querySnapshot.docs.map((documento) => ({
      id: documento.id,
      ...documento.data()
    }));

    setTimelineEvents(data);
  };

  const cargarSignals = async () => {
    const querySnapshot = await getDocs(collection(db, 'socialSignals'));

    const data = querySnapshot.docs.map((documento) => ({
      id: documento.id,
      ...documento.data()
    }));

    setSocialSignals(data);
  };

  const cargarSnapshots = async () => {
    const querySnapshot = await getDocs(collection(db, 'snapshots'));

    const data = querySnapshot.docs.map((documento) => ({
      id: documento.id,
      ...documento.data()
    }));

    setSnapshots(data);
  };

  useEffect(() => {
    // Solo el admin carga datos en VitaApp.
    // Si la sesión cambió a cliente (ej: al crear un cliente nuevo),
    // no pedimos nada: App.js redirige a ClientApp.
    if (userRol !== 'admin') {
      return;
    }

    cargarProyectos();
    cargarActores();
    cargarAlertas();
    cargarTimeline();
    cargarSignals();
    cargarSnapshots();

    const timer = setTimeout(() => {
      setLoading(false);
    }, 1200);

    return () => clearTimeout(timer);
  }, [userRol]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (mainRef.current) {
      mainRef.current.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  }, [activeView]);

    

  const activeProject = projects.find(
    (project) => project.id === activeProjectId
  );

  const projectActors = activeProjectId
    ? actors.filter((actor) => actor.projectId === activeProjectId)
    : [];

  const projectAlerts = activeProjectId
    ? alerts.filter((alerta) => alerta.projectId === activeProjectId)
    : [];

  const projectTimeline = activeProjectId
    ? timelineEvents.filter((evento) => evento.projectId === activeProjectId)
    : [];

  const projectSignals = activeProjectId
    ? socialSignals.filter((signal) => signal.projectId === activeProjectId)
    : [];

  const filteredActors = projectActors.filter((actor) =>
    territorialMatch(actor, territorialFilters)
  );

  const filteredAlerts = projectAlerts.filter((alerta) =>
    territorialMatch(alerta, territorialFilters)
  );

  const filteredSignals = projectSignals.filter((signal) =>
    territorialMatch(signal, territorialFilters)
  );

  const { processedActors } = useVitaEngine(
    projectActors,
    projectAlerts,
    projectSignals
  );

  const cognitiveAlerts = useCognitiveAlerts(processedActors);

  
  const processedFilteredActors = processedActors.filter((actor) =>
    territorialMatch(actor, territorialFilters)
  );

  const projectPrediction = buildProjectPrediction(snapshots, processedActors);

  const guardarProyecto = async () => {
    // Sacamos email, password y confirmación: NO se guardan en el proyecto
    const { clienteEmail, clientePassword, clientePasswordConfirm, ...datosProyecto } = newProject;

    const docRef = await addDoc(collection(db, 'proyectos'), datosProyecto);

    if (clienteEmail && clientePassword) {
      if (clientePassword !== clientePasswordConfirm) {
        alert('Proyecto guardado, pero las contraseñas no coinciden. El cliente no fue creado.');
      } else {
        try {
          // Crea el usuario Y su documento en una conexión temporal: NO te desloguea
          await createClientUser(clienteEmail, clientePassword, docRef.id);
          alert('Proyecto y cliente creados correctamente.');
        } catch (err) {
          alert('Proyecto guardado, pero error al crear usuario: ' + err.message);
        }
      }
    }

    setNewProject({
      nombre: '', cliente: '', tipo: '', region: '',
      estado: '', descripcion: '', clienteEmail: '', clientePassword: '', clientePasswordConfirm: ''
    });

    cargarProyectos();
  };

  const actualizarProyecto = async (id) => {
    await updateDoc(doc(db, 'proyectos', id), newProject);

    setNewProject({
      nombre: '',
      cliente: '',
      tipo: '',
      region: '',
      regionCodigo: '',
      estado: '',
      descripcion: ''
    });

    cargarProyectos();
  };

  const eliminarProyecto = async (id) => {
    const confirmar = window.confirm('¿Eliminar proyecto?');

    if (!confirmar) return;

    await deleteDoc(doc(db, 'proyectos', id));

    if (activeProjectId === id) {
      setActiveProjectId('');
    }

    cargarProyectos();
  };

  const iniciarNuevoActor = () => {
    setEditingActorId(null);

    setNewActor({
      nombre: '',
      tipo: '',
      riesgo: '',
      estado: '',
      comuna: '',
      sector: '',
      influencia: '',
      observaciones: '',
      latitud: '',
      longitud: '',
      tags: '',
      telefono: '',
      correo: '',
      whatsapp: '',
      sitioWeb: '',
      facebook: '',
      instagram: '',
      xTwitter: '',
      linkedin: ''
    });

    setShowActorCreation(true);

    setTimeout(() => {
      actorMapRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }, 150);
  };

  const guardarActor = async () => {
    if (editingActorId) {
      const { _vita, ...actorSinVita } = newActor;
      await updateDoc(doc(db, 'actores', editingActorId), {
        ...actorSinVita,
        projectId: activeProjectId
      });

      // Guardar snapshot con los datos NUEVOS recién editados,
      // no con processedActors (que aún tiene el estado anterior al re-render)
      await saveActorSnapshot(
        { ...actorSinVita, id: editingActorId, projectId: activeProjectId },
        activeProjectId
      );

      setEditingActorId(null);
    } else {
      const { _vita: _vitaNuevo, ...actorNuevoSinVita } = newActor;
      const docRef = await addDoc(collection(db, 'actores'), {
        ...actorNuevoSinVita,
        projectId: activeProjectId
      });

      await saveActorSnapshot(
        { ...newActor, id: docRef.id },
        activeProjectId
      );
    }

    setNewActor({
      nombre: '',
      tipo: '',
      riesgo: '',
      estado: '',
      comuna: '',
      sector: '',
      influencia: '',
      observaciones: '',
      latitud: '',
      longitud: '',
      tags: '',
      telefono: '',
      correo: '',
      whatsapp: '',
      sitioWeb: '',
      facebook: '',
      instagram: '',
      xTwitter: '',
      linkedin: ''
    });

    setShowActorCreation(false);
    cargarActores();
  };

  const editarActor = (actor) => {
    setEditingActorId(actor.id);
    setNewActor({ ...actor });
    setActiveView('actores');
    setShowActorCreation(true);

    setTimeout(() => {
      actorFormRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }, 150);
  };

  const eliminarActor = async (id) => {
    const confirmar = window.confirm('¿Eliminar stakeholder?');

    if (!confirmar) return;

    await deleteDoc(doc(db, 'actores', id));
    cargarActores();
  };

  const cancelarEdicionActor = () => {
    setEditingActorId(null);
    setShowActorCreation(false);

    setNewActor({
      nombre: '',
      tipo: '',
      riesgo: '',
      estado: '',
      comuna: '',
      sector: '',
      influencia: '',
      observaciones: '',
      latitud: '',
      longitud: '',
      tags: '',
      telefono: '',
      correo: '',
      whatsapp: '',
      sitioWeb: '',
      facebook: '',
      instagram: '',
      xTwitter: '',
      linkedin: ''
    });
  };

  const guardarAlerta = async () => {
    if (editingAlertId) {
      await updateDoc(doc(db, 'alertas', editingAlertId), {
        ...newAlert,
        projectId: activeProjectId
      });

      setEditingAlertId(null);
    } else {
      await addDoc(collection(db, 'alertas'), {
        ...newAlert,
        projectId: activeProjectId
      });
    }

    setNewAlert({
      titulo: '',
      nivel: '',
      actor: '',
      descripcion: '',
      estado: '',
      fecha: '',
      latitud: '',
      longitud: '',
      tags: ''
    });

    cargarAlertas();
  };

  const editarAlerta = (alerta) => {
    setEditingAlertId(alerta.id);
    setNewAlert({ ...alerta });
    setActiveView('alertas');

    setTimeout(() => {
      alertFormRef.current?.scrollIntoView({
        behavior: 'smooth'
      });
    }, 100);
  };

  const eliminarAlerta = async (id) => {
    const confirmar = window.confirm('¿Eliminar alerta?');

    if (!confirmar) return;

    await deleteDoc(doc(db, 'alertas', id));
    cargarAlertas();
  };

  const cancelarEdicionAlerta = () => {
    setEditingAlertId(null);

    setNewAlert({
      titulo: '',
      nivel: '',
      actor: '',
      descripcion: '',
      estado: '',
      fecha: '',
      latitud: '',
      longitud: '',
      tags: ''
    });
  };

  const guardarTimeline = async () => {
    if (editingTimelineId) {
      await updateDoc(doc(db, 'timeline', editingTimelineId), {
        ...newTimeline,
        projectId: activeProjectId
      });

      setEditingTimelineId(null);
    } else {
      await addDoc(collection(db, 'timeline'), {
        ...newTimeline,
        projectId: activeProjectId
      });
    }

    setNewTimeline({
      titulo: '',
      fecha: '',
      tipo: '',
      actor: '',
      descripcion: ''
    });

    cargarTimeline();
  };

  const editarTimeline = (evento) => {
    setEditingTimelineId(evento.id);
    setNewTimeline({ ...evento });
    setActiveView('timeline');

    setTimeout(() => {
      timelineFormRef.current?.scrollIntoView({
        behavior: 'smooth'
      });
    }, 100);
  };

  const eliminarTimeline = async (id) => {
    const confirmar = window.confirm('¿Eliminar evento?');

    if (!confirmar) return;

    await deleteDoc(doc(db, 'timeline', id));
    cargarTimeline();
  };

  const cancelarEdicionTimeline = () => {
    setEditingTimelineId(null);

    setNewTimeline({
      titulo: '',
      fecha: '',
      tipo: '',
      actor: '',
      descripcion: ''
    });
  };

  const guardarSignal = async () => {
    await addDoc(collection(db, 'socialSignals'), {
      ...newSignal,
      projectId: activeProjectId
    });

    setNewSignal({
      actor: '',
      red: '',
      sentimiento: '',
      riesgo: '',
      impacto: '',
      tipo: '',
      ubicacion: '',
      link: '',
      fecha: '',
      texto: '',
      latitud: '',
      longitud: '',
      tags: ''
    });

    cargarSignals();
  };

  function territorialMatch(item, filters) {
    const {
      selectedRegion,
      selectedComuna,
      selectedSector,
      selectedFoco
    } = filters;

    if (selectedRegion && item.region !== selectedRegion) return false;
    if (selectedComuna && item.comuna !== selectedComuna) return false;
    if (selectedSector && item.sector !== selectedSector) return false;

    if (selectedFoco) {
      const tags = String(item.tags || '')
        .toLowerCase()
        .split(',')
        .map((tag) => tag.trim());

      if (!tags.includes(selectedFoco.toLowerCase())) {
        return false;
      }
    }

    return true;
  }

  if (loading) {
    return <SplashScreen />;
  }

  return (
    <div style={styles.app}>
      <Sidebar
        activeView={activeView}
        setActiveView={handleNavClick}
      />

      <div ref={mainRef} style={styles.main}>
        {activeView === 'dashboard' && (
          <div id="territorial-report">
            <Dashboard
              totalActores={processedFilteredActors.length}
              actoresCriticos={
                processedFilteredActors.filter(
                  (actor) =>
                    actor.estado === 'Crítico' ||
                    actor.riesgo === 'Crítico' ||
                    ['Activación Territorial', 'Convergencia Narrativa', 'Escalamiento Territorial']
                      .includes(actor._vita?.state?.state)
                ).length
              }
              alertasActivas={
                filteredAlerts.filter(
                  (alerta) => alerta.estado !== 'Cerrada'
                ).length
              }
              altaInfluencia={
                filteredActors.filter(
                  (actor) => actor.influencia === 'Alta'
                ).length
              }
              totalEventos={filteredSignals.length}
              projectActors={processedFilteredActors}
              projectAlerts={filteredAlerts}
              projectSignals={filteredSignals}
              activeProject={activeProject}
              projects={projects}
              activeProjectId={activeProjectId}
              setActiveProjectId={setActiveProjectId}
              currentUser={currentUser}
              logout={logout}
              onOpenReport={() => setActiveView('reportes')}
            />
            
            <div ref={dashboardMapRef}>
              <MapSection
              projectActors={processedActors}
                projectAlerts={projectAlerts}
                projectSignals={projectSignals}
                projectTimeline={projectTimeline}
                mode="view"
                selectedEntity={selectedOperationalItem}
                territorialFilters={territorialFilters}
                setTerritorialFilters={setTerritorialFilters}
                activeProject={activeProject}
              />
            </div>

            <KnowledgeGraph
              processedActors={processedFilteredActors}
            />

            <CognitiveAlerts alerts={cognitiveAlerts} />

              </div>
        )}

        {activeView === 'territorios' && (
          <Projects
            projects={projects}
            activeProjectId={activeProjectId}
            setActiveProjectId={setActiveProjectId}
            newProject={newProject}
            setNewProject={setNewProject}
            guardarProyecto={guardarProyecto}
            eliminarProyecto={eliminarProyecto}
            actualizarProyecto={actualizarProyecto}
          />
        )}

        {activeView === 'actores' && (
          <>
            <div style={styles.actorActions}>
              <button
                style={styles.primaryButton}
                onClick={iniciarNuevoActor}
              >
                + Agregar Stakeholder
              </button>
            </div>

            <Actors
              actors={processedActors}
              editarActor={editarActor}
              eliminarActor={eliminarActor}
              projectAlerts={projectAlerts}
              projectSignals={projectSignals}
              projectTimeline={projectTimeline}
            />

            {showActorCreation && (
              <>
                <div ref={actorMapRef} style={styles.creationBlock}>
                  <MapSection
                    projectActors={projectActors}
                    projectAlerts={projectAlerts}
                    projectSignals={projectSignals}
                    projectTimeline={projectTimeline}
                    mode="select"
                    selectedCoordinates={{
                      latitud: newActor.latitud,
                      longitud: newActor.longitud
                    }}
                    onMapClick={(lat, lng) => {
                      setNewActor({
                        ...newActor,
                        latitud: lat,
                        longitud: lng
                      });

                      setTimeout(() => {
                        actorFormRef.current?.scrollIntoView({
                          behavior: 'smooth',
                          block: 'start'
                        });
                      }, 150);
                    }}
                  />
                </div>

                <div ref={actorFormRef}>
                  <ActorForm
                    newActor={newActor}
                    setNewActor={setNewActor}
                    guardarActor={guardarActor}
                    editingActorId={editingActorId}
                    cancelarEdicionActor={cancelarEdicionActor}
                    allActors={projectActors}
                  />
                </div>
              </>
            )}
          </>
        )}

        {activeView === 'alertas' && (
          <>
            <div ref={alertFormRef}>
              <AlertForm
                newAlert={newAlert}
                setNewAlert={setNewAlert}
                guardarAlerta={guardarAlerta}
                editingAlertId={editingAlertId}
                cancelarEdicionAlerta={cancelarEdicionAlerta}
                projectActors={projectActors}
                allAlerts={projectAlerts}
              />
            </div>

            <Alerts
              projectAlerts={projectAlerts}
              editarAlerta={editarAlerta}
              eliminarAlerta={eliminarAlerta}
            />
          </>
        )}

        {activeView === 'timeline' && (
          <>
            <div ref={timelineFormRef}>
              <TimelineForm
                newTimeline={newTimeline}
                setNewTimeline={setNewTimeline}
                guardarTimeline={guardarTimeline}
                editingTimelineId={editingTimelineId}
                cancelarEdicionTimeline={cancelarEdicionTimeline}
                projectActors={projectActors}
              />
            </div>

            <Timeline
              projectTimeline={projectTimeline}
              projectActors={projectActors}
              editarTimeline={editarTimeline}
              eliminarTimeline={eliminarTimeline}
              territorialFilters={territorialFilters}
              onSelectEvent={(evento) => {
                const actorRelacionado = projectActors.find((actor) => {
                  return (
                    String(actor.nombre || '').trim().toLowerCase() ===
                    String(evento.actor || '').trim().toLowerCase()
                  );
                });

                const itemActivo = actorRelacionado
                  ? {
                      ...actorRelacionado,
                      entityType: 'stakeholder',
                      panelType: 'Stakeholder territorial'
                    }
                  : {
                      ...evento,
                      entityType: 'event',
                      panelType: 'Evento territorial'
                    };

                setSelectedOperationalItem(itemActivo);
                setActiveView('dashboard');
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
            />
          </>
        )}

        {activeView === 'rrss' && (
          <>
            <div ref={socialFormRef}>
              <SocialMonitoring
                projectActors={projectActors}
                socialSignals={projectSignals}
                guardarSignal={guardarSignal}
                newSignal={newSignal}
                setNewSignal={setNewSignal}
              />
            </div>
          </>
        )}

        {activeView === 'busqueda' && (
          <GlobalSearch
            projectActors={projectActors}
            projectAlerts={projectAlerts}
            projectSignals={projectSignals}
            projectTimeline={projectTimeline}
          />
        )}

    {activeView === 'reportes' && (
  <ExecutiveReport
    activeProject={activeProject}
    activeProjectId={activeProjectId}
    projectActors={projectActors}
    processedActors={processedActors}
    projectAlerts={projectAlerts}
    projectTimeline={projectTimeline}
    projectSignals={projectSignals}
    territorialFilters={territorialFilters}
    projectPrediction={projectPrediction}
  />
)}

        {activeView === 'analytics' && (
          <AnalyticsPanel
            projectActors={projectActors}
            projectAlerts={projectAlerts}
            projectSignals={projectSignals}
          />
        )}
      </div>

      
      <TerritorialPanel
        selectedItem={
          selectedOperationalItem
            ? (processedActors.find(a => a.id === selectedOperationalItem.id) || selectedOperationalItem)
            : null
        }
        onClose={() => setSelectedOperationalItem(null)}
        projectActors={projectActors}
        projectAlerts={projectAlerts}
        projectSignals={projectSignals}
        projectTimeline={projectTimeline}
        onRelatedSelect={setSelectedOperationalItem}
      />
    </div>
  );
}

const styles = {
  app: {
    display: 'flex',
    fontFamily: 'Arial',
    background: '#071a2f',
    minHeight: '100vh'
  },

  main: {
    flex: 1,
    padding: '32px 42px 48px',
    background: '#071a2f',
    maxWidth: 'calc(100vw - 290px)',
    overflowX: 'hidden',
    overflowY: 'auto',
    height: '100vh'
  },

  actorActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginBottom: '22px'
  },

  primaryButton: {
    background: 'linear-gradient(135deg,#1fa97a,#0993b5)',
    color: 'white',
    border: 'none',
    padding: '14px 22px',
    borderRadius: '14px',
    fontWeight: 'bold',
    cursor: 'pointer'
  },

  creationBlock: {
    marginTop: '30px'
  }
};

export default VitaApp;